import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

// Lightweight mobile-friendly rich text editor (bold, italic, bullets, alignment).
// Stores/returns HTML. Admin-authored content only.
export function RichText({ value, onChange, placeholder = "", testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && (value || "") !== ref.current.innerHTML) ref.current.innerHTML = value || "";
    // init once on mount only — parent state is one-way from here after
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd) => (e) => {
    e.preventDefault();
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    onChange(ref.current.innerHTML);
  };

  const btn = "w-9 h-9 flex items-center justify-center rounded-lg border-2 border-charcoal bg-white active:bg-sunny md:hover:bg-sunny transition-colors shrink-0";
  return (
    <div className="border-2 border-charcoal rounded-xl overflow-hidden bg-white" data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="flex flex-wrap gap-1 p-1.5 bg-cream border-b-2 border-charcoal">
        <button type="button" onMouseDown={exec("bold")} className={btn} aria-label="Bold" data-testid={testid ? `${testid}-bold` : undefined}><Bold className="w-4 h-4" /></button>
        <button type="button" onMouseDown={exec("italic")} className={btn} aria-label="Italic" data-testid={testid ? `${testid}-italic` : undefined}><Italic className="w-4 h-4" /></button>
        <button type="button" onMouseDown={exec("underline")} className={btn} aria-label="Underline" data-testid={testid ? `${testid}-underline` : undefined}><Underline className="w-4 h-4" /></button>
        <button type="button" onMouseDown={exec("insertUnorderedList")} className={btn} aria-label="Bullet list" data-testid={testid ? `${testid}-bullets` : undefined}><List className="w-4 h-4" /></button>
        <span className="w-0.5 self-stretch bg-charcoal/15 mx-0.5 rounded" />
        <button type="button" onMouseDown={exec("justifyLeft")} className={btn} aria-label="Align left" data-testid={testid ? `${testid}-left` : undefined}><AlignLeft className="w-4 h-4" /></button>
        <button type="button" onMouseDown={exec("justifyCenter")} className={btn} aria-label="Align center" data-testid={testid ? `${testid}-center` : undefined}><AlignCenter className="w-4 h-4" /></button>
        <button type="button" onMouseDown={exec("justifyRight")} className={btn} aria-label="Align right" data-testid={testid ? `${testid}-right` : undefined}><AlignRight className="w-4 h-4" /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        data-placeholder={placeholder}
        data-testid={testid}
        className="rich-editor min-h-24 max-h-72 overflow-auto p-3 font-body text-base focus:outline-none"
      />
    </div>
  );
}

export const isEmptyHtml = (html) => !html || !html.replace(/<[^>]*>/g, "").replace(/&nbsp;|\s/g, "").trim();
